import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import EditIcon from "@material-ui/icons/Edit";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";
import { parseCourseSurfaceForPC } from "utils/enum-parser";
import TitledContainer from "components/TitledContainer";
import Loader from "components/Loader";
import { useRacehorse360Api } from "hooks/api";
import OverlayForm, { groupRaceTypes } from "./OverlayForm";
import ITrainerHorseDetailsPageState from "interfaces/TrainerHorseDetailsPageState";
import { setStatePreferredConditions } from "store/actions/trainerHorseDetailsPage";
import MatchedRaces from "./MatchedRaces";
import useStyles from "./styles";

interface Props {
  horse: racehorse360.IHorse;
  matchedRaces: racehorse360.IRaceCondition[];
  onOpenMatchedRacesPopover: () => void;
  refetchMatchedRaces: () => void;
}

export interface IStylesProps {
  isLastRace: boolean;
}

const PreferredCondition = (props: Props) => {
  const {
    horse,
    matchedRaces,
    onOpenMatchedRacesPopover,
    refetchMatchedRaces
  } = props;

  const [stylesProps, setStylesProps] = useState<IStylesProps>({
    isLastRace: false
  });

  //TODO rework on the separate classes or additional classes on condition
  // but don't use adapt styles here
  const classes = useStyles(stylesProps);
  const dispatch = useDispatch();

  const isOpenPreferredConditions = useSelector(
    (state: {
      trainerHorseDetailsPage: ITrainerHorseDetailsPageState;
    }): boolean => {
      return state.trainerHorseDetailsPage.preferredConditionsState;
    }
  );

  const [shouldUpdatePreferredConditions, setShouldUpdatePreferredConditions] =
    useState<boolean>(false);

  useEffect(() => {
    return () => {
      dispatch(setStatePreferredConditions(false));
    };
  }, []);

  const { usePullHorsePreferredConditions } = useRacehorse360Api();

  const {
    data: horsePCData,
    isLoading: isHorsePCLoading,
    isFetching: isHorsePCDataFetching
  } = usePullHorsePreferredConditions(`${horse.id}-PC`, {
    id: horse.id
  });

  const {
    preferredConditions,
    lastRacePreferredConditions,
    areLastRacePreferredConditions,
    conditionNote
  } = horsePCData || {};

  const hasPreferredConditions =
    preferredConditions?.raceDistances?.length ||
    preferredConditions?.raceTypes?.length ||
    preferredConditions?.surface ||
    conditionNote;

  const hasLastRacePreferredConditions =
    lastRacePreferredConditions?.raceDistances?.length ||
    lastRacePreferredConditions?.raceTypes?.length ||
    lastRacePreferredConditions?.surface ||
    conditionNote;

  const isLoading = isHorsePCLoading || isHorsePCDataFetching;

  useEffect(() => {
    if (!isLoading) {
      setStylesProps({
        isLastRace: areLastRacePreferredConditions
      });
    }
  }, [isLoading]);

  const handleEditClick = () => {
    dispatch(setStatePreferredConditions(true));
  };

  const handleOverlayClose = () => {
    dispatch(setStatePreferredConditions(false));
  };

  const renderPreferredConditions = () => {
    let output = (
      <Box className={clsx(classes.gridItem, classes.emptyGridItem)}>
        <Typography className={classes.placeholder}>No Preference</Typography>
      </Box>
    );

    if (areLastRacePreferredConditions && hasLastRacePreferredConditions) {
      output = (
        <>
          <Box className={classes.gridItem}>
            <Typography className={classes.gridItemTitle}>Last Race</Typography>
            <Box className={classes.gridItemContent}>
              <Typography className={clsx(classes.snack, classes.wide)}>
                {parseCourseSurfaceForPC(lastRacePreferredConditions.surface)}
              </Typography>

              {lastRacePreferredConditions.raceDistances.length > 0 &&
                lastRacePreferredConditions.raceDistances
                  .sort((rd1, rd2) => rd1.distance - rd2.distance)
                  .map(rd => (
                    <Typography key={rd.id} className={classes.snack}>
                      {rd.description}
                    </Typography>
                  ))}

              {lastRacePreferredConditions.raceTypes.length > 0 &&
                groupRaceTypes(lastRacePreferredConditions.raceTypes).map(
                  rt => (
                    <Typography key={rt.name} className={classes.snack}>
                      {rt.name}
                    </Typography>
                  )
                )}

              {lastRacePreferredConditions.claimingPrices.length > 0 &&
                lastRacePreferredConditions.claimingPrices
                  .sort((cp1, cp2) => cp1.minPrice - cp2.minPrice)
                  .map(cp => (
                    <Typography key={cp.id} className={classes.snack}>
                      {cp.description}
                    </Typography>
                  ))}
            </Box>

            {conditionNote && (
              <Box className={clsx(classes.gridItem, classes.gridItemNote)}>
                <Typography className={classes.gridItemTitle}>Note</Typography>
                <Typography
                  className={clsx(classes.gridItemContent, classes.note)}
                >
                  {conditionNote}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      );
    }

    if (!areLastRacePreferredConditions && hasPreferredConditions) {
      output = (
        <>
          <Box className={classes.gridItem}>
            <Typography className={classes.gridItemTitle}>Surface</Typography>
            <Box className={classes.gridItemContent}>
              <Typography className={clsx(classes.snack, classes.wide)}>
                {parseCourseSurfaceForPC(preferredConditions.surface)}
              </Typography>
            </Box>
          </Box>
          {preferredConditions.raceDistances.length > 0 && (
            <Box className={classes.gridItem}>
              <Typography className={classes.gridItemTitle}>
                Distances
              </Typography>
              <Box className={classes.gridItemContent}>
                {preferredConditions.raceDistances
                  .sort((rd1, rd2) => rd1.distance - rd2.distance)
                  .map(rd => (
                    <Typography key={rd.id} className={classes.snack}>
                      {rd.description}
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}
          {preferredConditions.raceTypes.length > 0 && (
            <Box className={classes.gridItem}>
              <Typography className={classes.gridItemTitle}>
                Conditions
              </Typography>
              <Box className={classes.gridItemContent}>
                {groupRaceTypes(preferredConditions.raceTypes).map(rt => (
                  <Typography key={rt.name} className={classes.snack}>
                    {rt.name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
          {preferredConditions.claimingPrices.length > 0 && (
            <Box className={classes.gridItem}>
              <Typography className={classes.gridItemTitle}>
                Claiming Price
              </Typography>
              <Box className={classes.gridItemContent}>
                {preferredConditions.claimingPrices
                  .sort((a, b) => a.minPrice - b.minPrice)
                  .map(cp => (
                    <Typography key={cp.id} className={classes.snack}>
                      {cp.description.toLowerCase().replace("to", "-")}
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}
          {conditionNote && (
            <Box className={clsx(classes.gridItem, classes.gridItemNote)}>
              <Typography className={classes.gridItemTitle}>Note</Typography>
              <Typography
                className={clsx(classes.gridItemContent, classes.note)}
              >
                {conditionNote}
              </Typography>
            </Box>
          )}
        </>
      );
    }

    return output;
  };

  return (
    <>
      <TitledContainer title={"Preferred Conditions"}>
        <Paper
          className={clsx(classes.paper, {
            ["emptyPreferredConditions"]:
              !hasPreferredConditions && !hasLastRacePreferredConditions
          })}
        >
          {isLoading && <Loader overlay />}
          <div className={classes.mainInfo}>
            <Button className={classes.editButton} onClick={handleEditClick}>
              <EditIcon className={classes.editIcon} />
              Edit
            </Button>
            <Box className={classes.grid}>{renderPreferredConditions()}</Box>
          </div>
          {hasPreferredConditions && (
            <MatchedRaces
              matchedRaces={matchedRaces}
              onOpenMatchedRacesPopover={onOpenMatchedRacesPopover}
            />
          )}
        </Paper>
      </TitledContainer>
      {!isLoading && isOpenPreferredConditions && (
        <OverlayForm
          open={true}
          onClose={handleOverlayClose}
          horse={horse}
          isLastRacePreferredConditions={areLastRacePreferredConditions}
          assignedPreferredConditions={preferredConditions || undefined}
          calculatedLastRacePreferredConditions={lastRacePreferredConditions}
          conditionNote={conditionNote}
          isPCLoading={isLoading}
          refetchMatchedRaces={refetchMatchedRaces}
          shouldUpdatePreferredConditions={shouldUpdatePreferredConditions}
          setShouldUpdatePreferredConditions={
            setShouldUpdatePreferredConditions
          }
        />
      )}
    </>
  );
};

export default PreferredCondition;
